/**
 * バグ転換（ops 依頼がバグチケットになったか）の定義を 1 箇所に閉じる共有ルール。
 *
 * 転換の唯一の根拠は関連チケットの **`investigation_result_bug`**（本件調査結果バグ）で、
 * `reference_bug` / `reference_ops` は参考リンクなので数えない。`classification` はこの射影で、
 * 独立した状態を持たない（＝紐付けを外せば転換も戻る）。
 *
 * 本件調査結果バグを足す入口は 3 つある: 画面の起票ボタン（product-api が起票と同時に紐付け）、関連チケット欄の
 * 「バグチケットを紐付け」（Backlog MCP 等で別途起票したチケットを key / URL で紐付け）、customer-success MCP の
 * `link_ops_bug_ticket`。どの入口も配列と分類を `applyOpsRelatedTicketUpsert` で同時に書き、key / URL の解釈は
 * `parseBacklogIssueRef` に寄せる。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼のバグ転換の定義（本件調査結果バグの有無から classification を射影）・関連チケット変更と分類の同時更新・Backlog チケット参照（key / URL）の正規化と、旧データ修復対象の抽出を共有する。本件調査結果バグは起票ボタン・関連チケット欄の手動紐付け・MCP link_ops_bug_ticket のいずれからも同じ関数で紐付く
 * @graph-connects none
 */
import { OPS_BACKLOG_SPACE_URL } from './backlog.js';
import { removeOpsRelatedTicket, upsertOpsRelatedTicket, } from './related-ticket.js';
/** Backlog issue key（例: AIRCLOSET-1234）の形。URL からの抽出と入力の正規化が同じ判定を使う。 @graph-connects none */
const BACKLOG_ISSUE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*-\d+$/;
/** Backlog チケット URL の issue key 直前までの prefix。 @graph-connects none */
const BACKLOG_VIEW_PREFIX = `${OPS_BACKLOG_SPACE_URL}/view/`;
/**
 * 関連チケットから起票分類（classification）を導出する。**「本件調査結果バグ」が 1 件でも紐付いていれば
 * `bug`、無ければ `null`**。
 *
 * バグ転換率はこの分類を数えるため、「依頼がバグになった」の定義がここ 1 箇所に閉じる。射影にしているのは、
 * 紐付け間違いを外したときに分類も戻るようにするため — stamp-only にすると KPI を上げることはできても
 * 下げられず、誤って付けた分が恒久的に残る。
 * @graph-connects none
 */
export function deriveOpsClassification(relatedTickets) {
    return relatedTickets.some((t) => t.kind === 'investigation_result_bug') ? 'bug' : null;
}
/** Backlog チケット URL（`<space>/view/<issueKey>`）から issue key を取り出す。形式不一致は null。 @graph-connects none */
export function backlogIssueKeyFromUrl(url) {
    if (!url.startsWith(BACKLOG_VIEW_PREFIX))
        return null;
    const key = url.slice(BACKLOG_VIEW_PREFIX.length).trim();
    return BACKLOG_ISSUE_KEY_PATTERN.test(key) ? key : null;
}
/**
 * 人が貼った Backlog チケット参照（issue key か `<space>/view/<KEY>` の URL）を issue key に正規化する。
 * 解釈できなければ null。
 *
 * 画面の入力欄と MCP の引数が同じ規則で受けるための単一 source。ブラウザのアドレスバーからコピーした URL は
 * 末尾スラッシュ・クエリ・`#comment-...` が付くことが多く、Slack から拾った key は小文字のこともあるので、
 * それらは吸収して正規の key（大文字）に揃える。別スペースの URL は推測で key を抜き出さず null にする
 * （他スペースのチケットをバグ転換として数えないため）。
 * @graph-connects none
 */
export function parseBacklogIssueRef(input) {
    const trimmed = input.trim();
    const fromUrl = trimmed.startsWith(BACKLOG_VIEW_PREFIX);
    const rawKey = fromUrl
        ? trimmed
            .slice(BACKLOG_VIEW_PREFIX.length)
            .replace(/[?#].*$/, '')
            .replace(/\/+$/, '')
        : trimmed;
    const key = rawKey.toUpperCase();
    return BACKLOG_ISSUE_KEY_PATTERN.test(key) ? key : null;
}
/**
 * 関連チケットを 1 件 upsert し、同時に起票分類を射影し直す。純関数。
 *
 * 配列と分類を別々に書き換えると、片方だけ更新する経路（例: MCP から紐付けたが分類を書き忘れる）が生まれ、
 * バグ転換が Firestore 上でずれる。product-api と MCP が同じ組で書くよう 1 関数にまとめる。
 * @graph-connects upsertOpsRelatedTicket [calls] 重複排除つき upsert
 * @graph-connects deriveOpsClassification [calls] 起票分類の射影
 */
export function applyOpsRelatedTicketUpsert(existing, entry) {
    // 置き換えは要素ごとなので、件名を持たない再紐付け（参考バグの昇格・画面 / MCP からの key だけの紐付け）で
    // 既にある件名が消えないよう引き継ぐ。
    const same = (existing ?? []).find((t) => t.target === entry.target && t.ref === entry.ref);
    const merged = entry.title === null && same?.title ? { ...entry, title: same.title } : entry;
    const relatedTickets = upsertOpsRelatedTicket(existing ?? [], merged);
    return { relatedTickets, classification: deriveOpsClassification(relatedTickets) };
}
/**
 * `(target, ref)` の関連チケットを外し、同時に起票分類を射影し直す。純関数。誤って付けた本件調査結果バグを
 * 外せば分類も戻る（`applyOpsRelatedTicketUpsert` と対）。
 * @graph-connects removeOpsRelatedTicket [calls] (target, ref) 一致の解除
 * @graph-connects deriveOpsClassification [calls] 起票分類の射影
 */
export function applyOpsRelatedTicketRemoval(existing, target, ref) {
    const relatedTickets = removeOpsRelatedTicket(existing ?? [], target, ref);
    return { relatedTickets, classification: deriveOpsClassification(relatedTickets) };
}
/**
 * 「バグチケットは起票済みなのに本件調査結果バグが紐付いていない」依頼を抽出する。
 *
 * この状態が生まれるのは、起票フローが URL 紐付け（確定）→ 関連チケット追加（**best-effort**）の順で書く
 * ため。後者が落ちると URL だけが残り、転換が計上されないまま気付けない。`backlogTicketUrl` を書くのは
 * 起票ユースケースだけなので、URL の存在は「この依頼から起票した」根拠として使える。
 * @graph-connects none
 */
export function planOpsBugConversionRepairs(candidates) {
    const repairs = [];
    for (const c of candidates) {
        const url = (c.backlogTicketUrl ?? '').trim();
        if (url === '')
            continue;
        if (deriveOpsClassification(c.relatedTickets ?? []) === 'bug')
            continue;
        const issueKey = backlogIssueKeyFromUrl(url);
        if (issueKey === null)
            continue;
        repairs.push({ id: c.id, issueKey, url });
    }
    return repairs;
}
//# sourceMappingURL=bug-conversion.js.map